/*
 * <select-dropdown> — drives the design-system dropdown menu (snippets/dropdown-menu.liquid in its
 * `dropdown-menu--field` variant, Figma node 6822:4125) as a drop-in replacement for a native
 * <select>'s popup.
 *
 * The native <select> stays in the DOM, visually hidden, as the value carrier — so existing form
 * and validation code keeps reading `.value` and listening for `change` unchanged. Picking an
 * option writes the value back and dispatches `input` + `change` (what a native pick fires), and
 * the trigger label re-syncs whenever the <select> changes, from anywhere.
 *
 * Expected children: the rendered dropdown shell (.dropdown-menu__trigger / __label / __option)
 * plus one <select>. `data-placeholder` on the host is shown while the value is empty.
 * Open/close, focus-out and Esc come from <details-disclosure> (script-details-disclosure.js).
 */
if (!customElements.get('select-dropdown')) {
  customElements.define(
    'select-dropdown',
    class SelectDropdown extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        this.label = this.querySelector('.dropdown-menu__label');
        this.select = this.querySelector('select');
        this.options = Array.from(this.querySelectorAll('.dropdown-menu__option'));
        if (!this.select || this.options.length === 0) return;

        this.options.forEach((option) => {
          option.addEventListener('click', () => this.selectOption(option));
        });
        this.select.addEventListener('change', () => this.syncFromSelect());
        this.syncFromSelect();
      }

      selectOption(option) {
        const value = option.dataset.value;
        if (this.select.value !== value) {
          this.select.value = value;
          this.select.dispatchEvent(new Event('input', { bubbles: true }));
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (this.details) this.details.removeAttribute('open');
      }

      syncFromSelect() {
        const selected = this.options.find((option) => option.dataset.value === this.select.value);

        if (this.label) {
          this.label.textContent = selected ? selected.textContent.trim() : this.dataset.placeholder || '';
          this.label.classList.toggle('dropdown-menu__label--placeholder', !selected);
        }

        this.options.forEach((option) => {
          const isSelected = option === selected;
          option.classList.toggle('dropdown-menu__option--selected', isSelected);
          const item = option.closest('li');
          if (item) item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
      }
    }
  );
}
